// Blocked users sync issue dialog component for the library

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import type { LibraryBlockedUsersSyncResult } from '../hooks';

type BlockedUsersSyncIssueDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetry: () => Promise<LibraryBlockedUsersSyncResult>;
  onReconnect: () => void;
};

const BlockedUsersSyncIssueDialog: React.FC<
  BlockedUsersSyncIssueDialogProps
> = ({ open, onOpenChange, onRetry, onReconnect }) => {
  const [retryingSync, setRetryingSync] = React.useState(false);

  const handleRetry = async (event: React.MouseEvent) => {
    event.preventDefault();
    setRetryingSync(true);
    try {
      await onRetry();
    } finally {
      setRetryingSync(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Blocked users unavailable</AlertDialogTitle>
          <AlertDialogDescription>
            We couldn&apos;t sync your blocked and banned users right now. This
            usually clears up on its own. Try again in a moment, or reconnect
            your Twitch account if it keeps failing.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={retryingSync}>
            Not now
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            variant="outline"
            disabled={retryingSync}
            onClick={onReconnect}
          >
            Reconnect Twitch
          </AlertDialogAction>
          <AlertDialogAction
            type="button"
            disabled={retryingSync}
            onClick={handleRetry}
          >
            {retryingSync ? 'Trying again...' : 'Try again'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BlockedUsersSyncIssueDialog;
